// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

// The app-side Athena client (WS4 Phase 3b). Implements the diagram's
// backend-agnostic DescribeFn by POSTing to /api/athena/describe and mapping the
// DescribeOutcome JSON onto a client AgentResult (message | unavailable). Kept in
// the app, not the diagram package, so @simlin/diagram carries no server-API
// dependency. Never throws: a network error, a non-2xx status, or a malformed
// body all map to a graceful `unavailable`.

import type { AgentResult, DescribeFn } from '@simlin/diagram/agent-conversation';

const DEFAULT_UNAVAIL = 'The scribe is unavailable right now.';
const ATHENA_DESCRIBE_PATH = '/api/athena/describe';

function str(x: unknown, fallback: string): string {
  return typeof x === 'string' && x.trim().length > 0 ? x : fallback;
}

/**
 * Map an HTTP status + parsed body from /api/athena/describe onto an AgentResult
 * (pure). Only a 200 `message` carrying text becomes a message; a tier block
 * (403), unconfigured service (503), bad status, or junk body is a graceful
 * unavailable.
 */
export function mapDescribe(status: number, data: unknown): AgentResult {
  const o: Record<string, unknown> = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
  if (status === 200 && o.kind === 'message') {
    return { kind: 'message', text: str(o.text, 'A system dynamics model.') };
  }
  if (status === 403) {
    return { kind: 'unavailable', reason: 'Athena needs the Member tier or higher.' };
  }
  if (status === 503 || o.kind === 'unavailable') {
    return { kind: 'unavailable', reason: str(o.reason, DEFAULT_UNAVAIL) };
  }
  return { kind: 'unavailable', reason: DEFAULT_UNAVAIL };
}

export interface AthenaDescriberDeps {
  fetchFn?: typeof fetch;
  path?: string;
}

/** Build the app's DescribeFn -- one POST to the server, mapped to an AgentResult. */
export function createAthenaDescriber(deps: AthenaDescriberDeps = {}): DescribeFn {
  const doFetch = deps.fetchFn ?? ((input: RequestInfo | URL, init?: RequestInit) => fetch(input, init));
  const path = deps.path ?? ATHENA_DESCRIBE_PATH;

  return async ({ model, modelName, history }) => {
    let res: Response;
    try {
      res = await doFetch(path, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ model, modelName, history }),
      });
    } catch {
      return { kind: 'unavailable', reason: DEFAULT_UNAVAIL };
    }

    let data: unknown;
    try {
      data = await res.json();
    } catch {
      data = undefined;
    }
    return mapDescribe(res.status, data);
  };
}
